import React from "react";
import { css } from "@linaria/core";
import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import { buildOptions, FilterWrapper, useSubStateSetter } from "@ui/Filter";
import FilterSelect from "@ui/Filter/FilterSelect";
import t from "../../../i18n";
import { activityOptionsFilter, ActivityTypeValidator } from "../../Activity/activityLabels";
import Reset from "@ui/Filter/Reset";
import { useAuthors } from "../../../api/useAuthors";
import { formatAuthorName } from "../../Activity/getActivityDescription";
import { uuid } from "diagnostics-lib/io";
import { useActivityEvents } from "../../../api/useActivityEvents";
import classNames from "classnames";
import { useFaultEventAccess } from "../Events/hooks/useFaultEventAccess";
import { useTranslations } from "../../../api/useTranslations";
import Switch from "@ui/Switch";
import { getActivityEventFilterKey } from "./eventFilter";
import { useSettings } from "../../../api/useSettings";

const FilteredValuesByFilterType = io.strict({
  types: io.array(ActivityTypeValidator),
  authors: io.array(uuid),
  eventIds: io.array(io.string),
});
type FilteredValuesByFilterType = io.TypeOf<typeof FilteredValuesByFilterType>;

const DEFAULT_FILTER_VALUES: FilteredValuesByFilterType = {
  types: [],
  authors: [],
  eventIds: [],
};

export const useFilteredValuesByFilterType = createGlobalState({
  storage: "session",
  key: "projectActivityFilteredValues",
  type: FilteredValuesByFilterType,
  scoped: true,
});

export const useHideAutomaticActions = createGlobalState({
  storage: "session",
  key: "projectHideAutomaticActions",
  defaultValue: true,
  type: io.boolean,
  scoped: true,
});

interface P {
  projectId: string;
  eventId: string | null;
  filteredValues: FilteredValuesByFilterType;
  setFilteredValues: (props: React.SetStateAction<FilteredValuesByFilterType>) => void;
  hideSystemUserActions: boolean;
  setHideAutomaticActions: (value: boolean) => void;
}

const ProjectActivityFilters = ({
  projectId,
  eventId,
  filteredValues,
  setFilteredValues,
  hideSystemUserActions,
  setHideAutomaticActions,
}: P) => {
  const translations = useTranslations();
  const hasFaultEventAccess = useFaultEventAccess();
  const settings = useSettings();
  const users = useAuthors(projectId);
  const userOptions = React.useMemo(
    () =>
      buildOptions(
        users.map(user => ({
          value: user.id,
          label: formatAuthorName(user.name),
          badge: user.deletedAt === null ? undefined : t("deleted"),
        })),
      ),
    [users],
  );

  const events = useActivityEvents(projectId);
  const visibleEvents = React.useMemo(
    () =>
      settings.MANUAL_EVENTS_DEV_MODE_ENABLED
        ? events
        : events.filter(event => !event.type.startsWith("MANUAL_FAULT_EVENT")),
    [events, settings.MANUAL_EVENTS_DEV_MODE_ENABLED],
  );
  const eventOptions = React.useMemo(
    () =>
      buildOptions(
        visibleEvents.map(event => ({
          value: getActivityEventFilterKey(event),
          label: formatAuthorName(event.name ?? ""),
          badge: event.type === "MANUAL_FAULT_EVENT" ? t("activity.event.badge.manual") : undefined,
        })),
      ),
    [visibleEvents],
  );

  const activityTypeOptions = React.useMemo(() => {
    const options = activityOptionsFilter(translations);
    if (settings.MANUAL_EVENTS_DEV_MODE_ENABLED) return options;
    return options.filter(option => !option.value.startsWith("MANUAL_FAULT_EVENT"));
  }, [settings.MANUAL_EVENTS_DEV_MODE_ENABLED, translations]);

  const setValuesEvents = useSubStateSetter(setFilteredValues, "eventIds");

  React.useEffect(() => {
    setFilteredValues(f => {
      if (!hasFaultEventAccess) return f.eventIds.length > 0 ? { ...f, eventIds: [] } : f;
      return f;
    });
  }, [hasFaultEventAccess, setFilteredValues]);

  return (
    <div className={filtersLayout}>
      <FilterWrapper className={classNames({ wrappable: true })}>
        <FilterSelect
          title={t("activity.selectFilter.activity")}
          options={activityTypeOptions}
          values={filteredValues.types}
          setValues={useSubStateSetter(setFilteredValues, "types")}
          filtered
        />
        <FilterSelect
          title={t("activity.selectFilter.authors")}
          options={userOptions}
          values={filteredValues.authors}
          setValues={useSubStateSetter(setFilteredValues, "authors")}
          filtered
        />
        {hasFaultEventAccess && eventId === null && (
          <FilterSelect
            title={t("activity.selectFilter.events")}
            options={eventOptions}
            values={filteredValues.eventIds}
            setValues={setValuesEvents}
            filtered
          />
        )}
        <Reset
          filterValues={filteredValues}
          setFilterValues={setFilteredValues}
          defaultFilterValues={DEFAULT_FILTER_VALUES}
        />
      </FilterWrapper>
      <Switch
        checked={hideSystemUserActions}
        onChange={e => setHideAutomaticActions(e.currentTarget.checked)}
        tooltipOverlay={t("activity.hide_automatic_actions.tooltip")}
      >
        {t("activity.hide_automatic_actions")}
      </Switch>
    </div>
  );
};
export default ProjectActivityFilters;

const filtersLayout = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;
